import React, { useEffect, useState } from "react";
import { ArrowLeft, Upload } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Utils from "../../utils";
import { notify } from "../../components/Common/Alerts";
import { decryptData } from "../../utils/crypto";
import {
  createCards,
  detailsCards,
  updateCards,
} from "../../services/Admin/cardsService";
import { getAllRewardProgramsList } from "../../services/Admin/commonService";
import Select from "react-select";

const AddCards = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    reward_program_id: "",
    bank: "",
    annual_fee: "",
    description: "",
    priority: "",
    is_featured: 0,
    is_active: 1,
    uuid: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    reward_program_id: "",
    bank: "",
    annual_fee: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [rewardProgramsList, setRewardProgramsList] = useState([]);
  const [cardDetails, setCardDetails] = useState("");

  const validateField = (name, value) => {
    switch (name) {
      case "reward_program_id":
        if (!value.trim()) return "Reward program is required";
        return "";

      case "name":
        if (!value.trim()) return "Card name is required";
        return "";

      case "bank":
        if (!value.trim()) return "Bank is required";
        return "";

      case "annual_fee":
        if (!value) return "Annual fees is required";
        if (isNaN(value)) return "Annual fees must be a number";
        return "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  useEffect(() => {
    getAllRewardProgramsList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);

        const formatted = decrypted.data.map((item) => ({
          value: item.id,
          label: item.name,
        }));

        setRewardProgramsList(formatted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(() => {
    if (uuid) {
      let data = {
        uuid: uuid,
      };
      detailsCards(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);
          setCardDetails(decrypted.data);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [uuid]);

  useEffect(() => {
    if (cardDetails) {
      setFormData({
        name: cardDetails?.name,
        reward_program_id: cardDetails?.reward_program_id,
        bank: cardDetails?.bank,
        annual_fee: cardDetails?.annual_fee,
        description: cardDetails?.description,
        priority: cardDetails?.priority,
        is_featured: cardDetails?.is_featured,
        is_active: cardDetails?.is_active,
        uuid: cardDetails?.uuid,
      });
    }
  }, [cardDetails]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      reward_program_id: validateField(
        "reward_program_id",
        formData.reward_program_id,
      ),
      name: validateField("name", formData.name),
      bank: validateField("bank", formData.bank),
      annual_fee: validateField("annual_fee", formData.annual_fee),
    };

    setErrors(newErrors);
    Utils.focusError();
    const hasErrors = Object.values(newErrors).some((err) => err);
    if (hasErrors) return;

    try {
      setBtnLoading(true);
      const payload = {
        name: formData.name,
        reward_program_id: formData.reward_program_id,
        bank: formData.bank,
        annual_fee: formData.annual_fee,
        description: formData.description,
        priority: formData.priority,
        is_featured: formData.is_featured,
        is_active: formData.is_active,
        ...(formData?.uuid && {
          uuid: formData.uuid,
        }),
      };

      const apiCall = formData?.uuid ? updateCards : createCards;

      const response = await apiCall(payload);
      const decrypted = decryptData(response.data.data);
      notify.success(decrypted.message);
      setFormData({
        name: "",
        reward_program_id: "",
        bank: "",
        annual_fee: "",
        description: "",
        priority: "",
        is_featured: 0,
        is_active: 1,
        uuid: "",
      });
      setErrors({
        name: "",
        reward_program_id: "",
        bank: "",
        annual_fee: "",
      });
      navigate("/cards");
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  const getSelectedOption = (list, value) =>
    list.find((opt) => String(opt.value) === String(value)) || null;

  return (
    <div className="pb-24 md:pb-0 transition-colors duration-300">
      <div className="w-full bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm p-5 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/cards")}
            className="p-2 -ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl md:text-2xl font-black text-slate-800 dark:text-white tracking-tight">
            {uuid ? "Update Card" : "Add Card"}
          </h2>
        </div>

        <div className="space-y-6 md:space-y-8">
          <div>
            <h3 className="text-[10px] md:text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-5 border-b border-slate-50 dark:border-slate-700/50 pb-2">
              Card Information
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <label htmlFor="name" className="input-label">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="input-field"
                    placeholder="Enter card name"
                    value={formData?.name || ""}
                    onChange={handleChange}
                    maxLength={100}
                  />
                  {errors.name && (
                    <span className="text-danger">{errors.name}</span>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="bank" className="input-label">
                    Bank <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="bank"
                    className="input-field"
                    placeholder="Enter bank name"
                    value={formData?.bank || ""}
                    onChange={handleChange}
                    maxLength={100}
                  />
                  {errors.bank && (
                    <span className="text-danger">{errors.bank}</span>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="annual_fee" className="input-label">
                    Annual fee <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="annual_fee"
                    className="input-field"
                    placeholder="Enter annual fee"
                    value={formData?.annual_fee || ""}
                    onChange={handleChange}
                    maxLength={100}
                  />
                  {errors.annual_fee && (
                    <span className="text-danger">{errors.annual_fee}</span>
                  )}
                </div>
              </div>

              <div className="space-y-5">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase">
                    Reward program <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={rewardProgramsList}
                    value={getSelectedOption(
                      rewardProgramsList,
                      formData.reward_program_id,
                    )}
                    onChange={(selected) => {
                      setFormData((prev) => ({
                        ...prev,
                        reward_program_id: String(selected?.value ?? ""),
                      }));
                    }}
                    placeholder="Select reward program"
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>

              <div className="lg:col-span-2 space-y-1.5">
                <label
                  htmlFor="description"
                  className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase"
                >
                  Description
                </label>
                <textarea
                  className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700 text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm outline-none min-h-[100px] transition-all focus:border-orange-500/50 dark:focus:border-orange-600"
                  placeholder="Enter program description..."
                  name="description"
                  value={formData?.description || ""}
                  onChange={handleChange}
                  rows={3}
                />
              </div>

              <div className="lg:col-span-2 space-y-3">
                <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase">
                  Status <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  {["Active", "Inactive"].map((status) => (
                    <label
                      key={status}
                      className="flex items-center gap-2 cursor-pointer group"
                    >
                      <div className="relative flex items-center justify-center">
                        <input
                          type="radio"
                          name="status"
                          className="peer shrink-0 appearance-none w-4 h-4 border border-slate-300 dark:border-slate-600 rounded-full bg-white dark:bg-slate-900 checked:border-orange-500 dark:checked:border-orange-600 transition-all"
                          defaultChecked={status === "Active"}
                        />
                        <div className="absolute w-2 h-2 rounded-full bg-orange-500 opacity-0 peer-checked:opacity-100 transition-opacity" />
                      </div>
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-slate-200 transition-colors">
                        {status}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-4 pt-6 mt-6 border-t border-slate-50 dark:border-slate-700/50">
            <button
              type="button"
              onClick={() => navigate("/cards")}
              className="flex-1 md:flex-none px-6 py-3 rounded-xl font-black text-xs text-slate-400 dark:text-slate-500"
            >
              Cancel
            </button>
            <button
              type="button"
              form="reward-form"
              className="flex-[2] md:flex-none px-8 py-3 bg-[#D4AF37] text-white rounded-xl font-black text-xs shadow-lg shadow-orange-200/50"
              onClick={handleSubmit}
              disabled={btnLoading}
            >
              {btnLoading && (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {btnLoading
                ? "submitting..."
                : formData.uuid
                  ? "Update"
                  : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCards;
